import { ImageEdit20Regular } from "@fluentui/react-icons";
import { useRichTextEditorContext } from "mui-tiptap";
import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Menu,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Divider,
  Tooltip,
} from "@mui/material";
import { Percent as PercentIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

type MenuButtonImageResizerProps = {
  tooltipLabel: string;
  ariaLabel: string;
};

const MIN_SIZE = 10;
const MAX_SIZE = 100;
const isValidInput = (value: string) =>
  value === "" ||
  (/^\d+(\.\d{0,2})?$/.test(value) && parseFloat(value) <= MAX_SIZE);
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

export default function MenuButtonImageResizer({
  tooltipLabel,
  ariaLabel,
}: MenuButtonImageResizerProps) {
  const editor = useRichTextEditorContext();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [width, setWidth] = useState("100");
  const [height, setHeight] = useState("100");
  const [proportional, setProportional] = useState(true);
  const [isImageSelected, setIsImageSelected] = useState(false);

  const handleWidthChange = useCallback((value: string) => {
    if (isValidInput(value)) setWidth(value);
  }, []);

  const handleWidthBlur = useCallback(() => {
    if (width && parseFloat(width) < MIN_SIZE) {
      setWidth(MIN_SIZE.toString());
    }
  }, [width]);

  const handleHeightChange = useCallback((value: string) => {
    if (isValidInput(value)) setHeight(value);
  }, []);

  const handleHeightBlur = useCallback(() => {
    if (height && parseFloat(height) < MIN_SIZE) {
      setHeight(MIN_SIZE.toString());
    }
  }, [height]);

  useEffect(() => {
    if (!editor) return;

    const updateImageSelection = () => {
      const node = editor.state.doc.nodeAt(editor.state.selection.from);
      setIsImageSelected(node?.type.name === "image");

      if (node?.type.name === "image" && !anchorEl) {
        const style = node.attrs.style || "";
        const w = style.match(/width:\s*(\d+(?:\.\d+)?)%/)?.[1];
        const h = style.match(/height:\s*(\d+(?:\.\d+)?)%/)?.[1];

        const currentWidth = w ? parseFloat(w) : 100;
        setWidth(currentWidth.toString());

        // Only update height if it exists in the style, otherwise keep the current value
        if (h) {
          setHeight(parseFloat(h).toString());
        }
      }
    };

    editor.on("selectionUpdate", updateImageSelection);
    updateImageSelection();
    return () => {
      editor.off("selectionUpdate", updateImageSelection);
    };
  }, [editor, anchorEl]);

  const handleProportionalChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setProportional(event.target.checked);
    },
    []
  );

  const handleApply = useCallback(() => {
    if (!editor) return;

    const node = editor.state.doc.nodeAt(editor.state.selection.from);
    if (node?.type.name !== "image") return;

    const widthValue = clamp(parseFloat(width) || 100, MIN_SIZE, MAX_SIZE);
    const heightValue = clamp(parseFloat(height) || 100, MIN_SIZE, MAX_SIZE);

    const styleObj = (node.attrs.style || "")
      .split(";")
      .reduce((acc: Record<string, string>, style: string) => {
        const [key, value] = style.split(":").map((s) => s.trim());
        if (key && value) acc[key] = value;
        return acc;
      }, {});

    styleObj.width = `${widthValue}%`;
    delete styleObj.height;

    if (proportional) {
      delete styleObj["aspect-ratio"];
      delete styleObj["object-fit"];
    } else {
      styleObj["aspect-ratio"] = (widthValue / heightValue).toFixed(6);
      styleObj["object-fit"] = "fill";
    }

    const from = editor.state.selection.from;
    editor
      .chain()
      .setTextSelection({ from, to: from + node.nodeSize })
      .updateAttributes("image", {
        style: Object.entries(styleObj)
          .map(([k, v]) => `${k}: ${v}`)
          .join("; "),
      })
      .setNodeSelection(from) // Use setNodeSelection to keep the image highlighted
      .run();

    setAnchorEl(null);
  }, [editor, width, height, proportional]);

  return (
    <>
      <Tooltip title={tooltipLabel}>
        <Button
          aria-label={ariaLabel}
          disabled={!editor?.isEditable || !isImageSelected}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            minWidth: "auto",
            padding: "4px 8px",
            color: "text.primary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <ImageEdit20Regular />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, fontWeight: "bold", color: "text.secondary" }}
        >
          {t("resizeImage")}
        </Typography>
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={`${t("width")} (%)`}
              value={width}
              onChange={(e) => handleWidthChange(e.target.value)}
              onBlur={handleWidthBlur}
              placeholder="100"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <PercentIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            {!proportional && (
              <TextField
                fullWidth
                size="small"
                label={`${t("height")} (%)`}
                value={height}
                onChange={(e) => handleHeightChange(e.target.value)}
                onBlur={handleHeightBlur}
                placeholder="100"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <PercentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={proportional}
                onChange={handleProportionalChange}
                size="small"
              />
            }
            label={t("maintainAspectRatio")}
            sx={{ mt: 1 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            {t("imageWidthInfo")}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button size="small" onClick={() => setAnchorEl(null)}>
            {t("cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={!width || (!proportional && !height)}
          >
            {t("apply")}
          </Button>
        </Box>
      </Menu>
    </>
  );
}
